import {
  Grid,
  IconButton,
  LinearProgress,
  Card,
  CardContent,
  Typography,
  Tooltip,
  Stack,
} from "@mui/material";
import Box from "@mui/material/Box";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { MdOutlineClear } from "react-icons/md";
import ResponsiveDataGrid from "../../../../../components/MobileList/ResponsiveDataGrid";
import DateRangepicker from "../../../../../helpers/dateRangePicker";
import { SelectLeaveStatus } from "../../../../../components/Selectors/SelectLeaveStatus";
import { SelectRepresentative } from "../../../../../components/Selectors/SelectRepresentative";
import {
  LeaveAdmin,
  setApproveDialog,
  setSearchBody,
} from "../../../../../app/slices/leaveAdminSlice";
import {
  getRowStatus,
  isPending,
  statusColorOf,
  STATUS_KIND,
} from "../../../leaveStatus";

/** Covers both the admin index and the approve/reject calls. */
const LEAVE_ADMIN_PERMISSION = "leave_application";

const LeaveAdminTabel = () => {
  const dispatch = useDispatch();

  const [search, setSearch] = useState({
    user: null,
    status: null,
    start_date: null,
    end_date: null,
  });
  const [pageSize, setPageSize] = useState(15);
  const [currentPage, setCurrentPage] = useState(0);

  const _Leaves = useSelector((state) => state.leaveAdmin.leaves);
  const loading = useSelector((state) => state.leaveAdmin.leaves.loading);
  const _SearchBody = useSelector((state) => state.leaveAdmin.searchBody);
  const userRoles = useSelector((state) => state.user.userInfo.user_roles);
  const userPermissions = useSelector(
    (state) => state.user.userInfo.permissions,
  );

  // Same rule as the route guard — super_admin or the permission, with no
  // admin-role bypass — so the list is never requested by a user the API
  // would reject anyway.
  const canManageLeave =
    userRoles?.includes("super_admin") ||
    userPermissions?.some((p) => p.name === LEAVE_ADMIN_PERMISSION);

  const columns = [
    {
      field: "index",
      headerName: "#",
      flex: 0.2,
    },
    {
      field: "user_name",
      headerName: "المندوب",
      flex: 1,
    },
    {
      field: "start_date",
      headerName: "تاريخ البداية",
      flex: 1,
      renderCell: (params) => (
        <>
          {params.row.start_date
            ? dayjs(params.row.start_date).format("YYYY-MM-DD")
            : null}
        </>
      ),
    },
    {
      field: "end_date",
      headerName: "تاريخ النهاية",
      flex: 1,
      renderCell: (params) => (
        <>
          {params.row.end_date
            ? dayjs(params.row.end_date).format("YYYY-MM-DD")
            : null}
        </>
      ),
    },
    {
      field: "total_days",
      headerName: "عدد الايام",
      flex: 0.7,
    },
    {
      field: "reason",
      headerName: "السبب",
      flex: 1.5,
    },
    {
      field: "status_name",
      headerName: "حالة الطلب",
      flex: 1,
      renderCell: (params) => {
        const status = getRowStatus(params.row);
        return (
          <Typography color={statusColorOf(status)}>
            {params.row.status_name}
          </Typography>
        );
      },
    },
    {
      field: "comments",
      headerName: "الملاحظات",
      flex: 1,
    },
    {
      field: "approved_by_name",
      headerName: "مدقق الطلب",
      flex: 1,
      renderCell: (params) =>
        params.row.approved_by_name ? (
          <Stack
            direction="column"
            spacing={0.5}
            justifyContent="center"
            alignItems="center"
          >
            <Typography>{params.row.approved_by_name}</Typography>
            {params.row.approved_at ? (
              <Typography variant="caption">
                {dayjs(params.row.approved_at).format("YYYY-MM-DD")}
              </Typography>
            ) : null}
          </Stack>
        ) : null,
    },
    {
      field: "created_at",
      headerName: "تاريخ الطلب",
      flex: 1,
      renderCell: (params) => (
        <>
          {params.row.created_at
            ? dayjs(params.row.created_at).format("YYYY-MM-DD")
            : null}
        </>
      ),
    },
    {
      field: "operations",
      headerName: "العمليات",
      flex: 1,
      renderCell: (params) => {
        const pending = isPending(params.row);
        if (!canManageLeave) return null;
        return (
          <>
            <Tooltip title="الموافقة على الطلب">
              <span>
                <IconButton
                  disabled={!pending}
                  onClick={() =>
                    dispatch(
                      setApproveDialog({
                        data: params.row,
                        kind: STATUS_KIND.approved,
                      }),
                    )
                  }
                >
                  <IoMdCheckmarkCircleOutline
                    color={pending ? "green" : undefined}
                  />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="رفض الطلب">
              <span>
                <IconButton
                  disabled={!pending}
                  onClick={() =>
                    dispatch(
                      setApproveDialog({
                        data: params.row,
                        kind: STATUS_KIND.rejected,
                      }),
                    )
                  }
                >
                  <AiOutlineCloseCircle color={pending ? "red" : undefined} />
                </IconButton>
              </span>
            </Tooltip>
          </>
        );
      },
    },
  ];

  useEffect(() => {
    if (!canManageLeave) return;
    const timeout = setTimeout(() => {
      dispatch(LeaveAdmin.getAll(_SearchBody));
    }, 500);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_SearchBody, canManageLeave]);

  useEffect(() => {
    dispatch(
      setSearchBody({
        user_id: search.user?.id ?? "",
        status_id: search.status?.id ?? "",
        start_date: search.start_date,
        end_date: search.end_date,
        skip: currentPage * pageSize,
        take: pageSize,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, search]);

  return (
    <Card>
      <CardContent>
        <Grid container rowSpacing={1} columnSpacing={2}>
          <Grid item xs={12} md={2}>
            <SelectRepresentative
              value={search.user}
              onChange={(e, newValue) => {
                setSearch({
                  ...search,
                  user: newValue ? newValue : null,
                });
                setCurrentPage(0);
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <SelectLeaveStatus
              value={search.status}
              onChange={(e, newValue) => {
                setSearch({
                  ...search,
                  status: newValue ? newValue : null,
                });
                setCurrentPage(0);
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <DateRangepicker
              startDate={search.start_date}
              endDate={search.end_date}
              handleOnChange={(searchDate) => {
                setSearch({
                  ...search,
                  start_date: dayjs(searchDate[0].startDate).format(
                    "YYYY-MM-DD",
                  ),
                  end_date: dayjs(searchDate[0].endDate).format("YYYY-MM-DD"),
                });
                setCurrentPage(0);
              }}
            />
          </Grid>
          <Grid item xs="auto" md={1}>
            <Tooltip title="الغاء الفلتر">
              <IconButton
                onClick={() => {
                  setSearch({
                    user: null,
                    status: null,
                    start_date: null,
                    end_date: null,
                  });
                  setPageSize(15);
                  setCurrentPage(0);
                }}
              >
                <MdOutlineClear />
              </IconButton>
            </Tooltip>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ height: { xs: "auto", md: "70vh" }, width: "100%" }}>
              <ResponsiveDataGrid
                gridHeight={null}
                rows={
                  _Leaves.data?.map((x, index) => ({
                    ...x,
                    index: index + 1,
                  })) ?? []
                }
                columns={columns}
                pageSize={pageSize}
                page={currentPage}
                rowCount={_Leaves.total}
                loading={loading}
                rowHeight={75}
                pagination
                paginationMode="server"
                selectionModel={[]}
                onPageChange={(newPage) => {
                  setCurrentPage(newPage);
                }}
                rowsPerPageOptions={[15, 25, 50, 100]}
                onPageSizeChange={(newPageSize) => {
                  setPageSize(newPageSize);
                }}
                components={{
                  LoadingOverlay: LinearProgress,
                }}
                mobile={{
                  titleField: "user_name",
                  subtitleField: "start_date",
                  badgeField: "status_name",
                  noteField: "reason",
                  fullWidthFields: ["comments"],
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default LeaveAdminTabel;
